import type {
  AuthParams,
  NoteState,
  ShareInfo,
  SyncCatchUp,
  AuthStatus,
  BackupStatus,
  EmbedConfig,
  NotesResponse,
  PostVersionRequest,
  PutNoteRequest,
  Session,
  SetupRequest,
  VaultIndex,
  VersionMeta,
} from '../../../shared/protocol.ts';

export class OfflineError extends Error {
  constructor() {
    super('Keine Verbindung');
    this.name = 'OfflineError';
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: unknown,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

let token: string | null = null;

export function setToken(value: string | null): void {
  token = value;
}

/** Für Wege, die an der JSON-Hülle vorbeigehen — etwa das Hochladen von Anhängen. */
export function getToken(): string {
  return token ?? '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new OfflineError();
  }
  if (response.status === 204) return undefined as T;
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const code =
      body && typeof body === 'object' && 'error' in body ? String(body.error) : 'unknown';
    throw new ApiError(response.status, code, body);
  }
  return body as T;
}

/** Für Antworten, die keine JSON-Hülle haben — versiegelte Dateien etwa. */
async function requestBytes(path: string): Promise<ArrayBuffer> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch {
    throw new OfflineError();
  }
  if (!response.ok) throw new ApiError(response.status, 'download_failed', null);
  return response.arrayBuffer();
}

const json = (data: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(data) });

export const api = {
  authStatus: () => request<AuthStatus>('/auth/status'),
  /** Ohne Anmeldung: Die Liste steht ohnehin im CSP-Header jeder Seite. */
  embedConfig: () => request<EmbedConfig>('/embed/config'),
  authParams: () => request<AuthParams>('/auth/params'),
  setup: (data: SetupRequest) => request<Session>('/auth/setup', json(data)),
  login: (authKey: string) => request<Session>('/auth/login', json({ authKey })),
  recover: (recoveryAuthKey: string) => request<Session>('/auth/recover', json({ recoveryAuthKey })),
  changePassword: (data: {
    kdfSalt: string;
    kdfParams: unknown;
    authKey: string;
    wrappedVaultKey: string;
  }) => request<{ ok: true }>('/auth/password', json(data)),

  listNotes: (since: number) => request<NotesResponse>(`/notes?since=${since}`),
  putNote: (id: string, data: PutNoteRequest) =>
    request<{ ok: true; updatedAt: number }>(`/notes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  noteState: (id: string, since: number) => request<NoteState>(`/notes/${id}/state?since=${since}`),
  postUpdate: (id: string, ciphertext: string, deviceId: string) =>
    request<{ ok: true; seq: number }>(`/notes/${id}/updates`, json({ ciphertext, deviceId })),
  putSnapshot: (id: string, ciphertext: string, seq: number) =>
    request<{ ok: true; removed: number }>(`/notes/${id}/snapshot`, json({ ciphertext, seq })),
  catchUp: (since: number) => request<SyncCatchUp>(`/notes/sync/catchup?since=${since}`),
  deleteNote: (id: string) => request<{ ok: true }>(`/notes/${id}`, { method: 'DELETE' }),
  restoreNote: (id: string) => request<{ ok: true }>(`/notes/${id}/restore`, { method: 'POST' }),

  listVersions: (noteId: string) => request<VersionMeta[]>(`/notes/${noteId}/versions`),
  getVersion: (noteId: string, versionId: string) =>
    request<{ payload: string; createdAt: number }>(`/notes/${noteId}/versions/${versionId}`),
  postVersion: (noteId: string, data: PostVersionRequest) =>
    request<{ ok: true }>(`/notes/${noteId}/versions`, json(data)),
  pruneVersions: (noteId: string, ids: string[]) =>
    request<{ ok: true; removed: number }>(`/notes/${noteId}/versions/prune`, json({ ids })),

  runBackup: () => request<{ ok: true }>('/vault/backup-run', { method: 'POST' }),

  transcribeStatus: () => request<{ available: boolean }>('/transcribe/status'),
  transcribe: (audio: Blob) =>
    request<{ text: string }>('/transcribe', {
      method: 'POST',
      body: audio,
      headers: { 'content-type': 'application/octet-stream' },
    }),

  listShares: () => request<ShareInfo[]>('/shares'),
  putShare: (id: string, data: { noteId: string; payload: string; expiresAt: number | null }) =>
    request<ShareInfo>(`/shares/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteShare: (id: string) => request<{ ok: true }>(`/shares/${id}`, { method: 'DELETE' }),

  getIndex: () => request<VaultIndex>('/vault/index'),
  putIndex: (payload: string, updatedAt: number) =>
    request<{ ok: true }>('/vault/index', { method: 'PUT', body: JSON.stringify({ payload, updatedAt }) }),
  backupStatus: () => request<BackupStatus>('/vault/backup-status'),

  // --- Versiegelter Eingang (docs/KONZEPT.md, Abschnitt 20) ---
  inboxKey: () => request<{ publicKey: string | null }>('/inbox/key'),
  setInboxKey: (publicKey: string) =>
    request<{ ok: true; verworfen: number }>('/inbox/key', {
      method: 'PUT',
      body: JSON.stringify({ publicKey }),
    }),
  inbox: () =>
    request<{
      items: { id: string; sealed: string; hatDatei: boolean; bytes: number; eingang: number }[];
    }>('/inbox'),
  inboxFile: async (id: string) => new Uint8Array(await requestBytes(`/inbox/${id}/datei`)),
  ackInbox: (id: string) => request<{ ok: true }>(`/inbox/${id}`, { method: 'DELETE' }),
  inboxSenders: () =>
    request<{ absender: { id: string; name: string; angelegt: number; zuletzt: number | null }[] }>(
      '/inbox/absender',
    ),
  newInboxSender: (name: string) =>
    request<{ id: string; token: string }>('/inbox/absender', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  deleteInboxSender: (id: string) =>
    request<{ ok: true }>(`/inbox/absender/${id}`, { method: 'DELETE' }),

  /** Einlieferer, die Aufträge annehmen — mit dem Schlüssel, für den zu versiegeln ist. */
  arbeiter: () =>
    request<{ arbeiter: { id: string; name: string; jobKey: string; zuletzt: number | null }[] }>(
      '/inbox/arbeiter',
    ),
  neuerAuftrag: (senderId: string, sealed: string) =>
    request<{ id: string | null }>('/inbox/auftrag', {
      method: 'POST',
      body: JSON.stringify({ senderId, sealed }),
    }),
  auftraege: () => request<{ offen: number; laufend: number }>('/inbox/auftraege'),

  /** Überschrift für eine diktierte Notiz. `null` heißt: selbst bilden. */
  title: (text: string) =>
    request<{ title: string | null }>('/title', { method: 'POST', body: JSON.stringify({ text }) }),

  ocrStatus: () => request<{ available: boolean; model: string }>('/ocr/status'),
  /** Ein Seitenbild zur Erkennung. Geht nur auf ausdrücklichen Wunsch hinaus. */
  ocr: (image: string, mime: string) =>
    request<{ text: string }>('/ocr', { method: 'POST', body: JSON.stringify({ image, mime }) }),

  pushConfig: () =>
    request<{ available: boolean; publicKey: string | null; horizonDays: number }>('/push/config'),
  pushSubscribe: (keys: { endpoint: string; p256dh: string; auth: string }) =>
    request<{ id: string }>('/push/subscribe', { method: 'POST', body: JSON.stringify(keys) }),
  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: true }>('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  pushSchedule: (endpoint: string, entries: { ref: string; fireAt: number; payload: string }[]) =>
    request<{ ok: true; stored: number }>('/push/schedule', {
      method: 'PUT',
      body: JSON.stringify({ endpoint, entries }),
    }),
  pushScheduleInfo: (endpoint: string) =>
    request<{ count: number; next: number | null }>(
      `/push/schedule?endpoint=${encodeURIComponent(endpoint)}`,
    ),
  pushTest: (endpoint: string) =>
    request<{ ok: true }>('/push/test', { method: 'POST', body: JSON.stringify({ endpoint }) }),
};
